import recorderWorkletUrl from './recorder.worklet.ts?worker&url';
import { generateSweep, sweepSpec, type Sweep, type SweepPresetName } from '../dsp/sweep.ts';
import { MEASUREMENT_CONSTRAINTS, captureBlocker } from './capture.ts';
import { createCueTrack, cueDurationSec, type CueTrack } from './cues.ts';
import {
  chirpStartSec,
  gapSec,
  idealHeadingRad,
  pacingCues,
  paceVerdict,
  scanTimeline,
  tiltCheckpoints,
  TILT_TARGET_DEG,
  type PaceVerdict,
  type Timeline,
} from '../dsp/pacing.ts';
import { trackOrientation, type OrientationStatus, type OrientationTracker } from '../sensors/orientation.ts';
import { applyMat3, type Mat3 } from '../dsp/geom3.ts';

/**
 * A scan: a train of chirps fired while the user turns, each tagged with the
 * orientation the phone held when it went out.
 *
 * The single-shot capture opens a context, plays one sweep, tears everything
 * down, and can afford to be leisurely about it. This cannot. The whole scan
 * has to fit inside one comfortable turn of the body - much over twenty-five
 * seconds and people stop turning smoothly, or stop entirely - so the audio
 * graph is built once and the chirps are scheduled against the context's own
 * clock rather than a timer, which drifts.
 *
 * Recording runs continuously through the whole scan and is cut up afterwards.
 * That matters: the alternative, starting and stopping the recorder around
 * each chirp, loses samples at every boundary, and a hole in a matched-filter
 * measurement is a hole in the impulse response.
 *
 * Three things happen around the chirping rather than inside it, and all three
 * are about the trajectory rather than the audio.
 *
 * The scan opens with a **ready beat**. The button is pressed with the phone in
 * front of the user's face, and the gesture the whole reconstruction assumes -
 * arm out, screen towards you - takes a second or two to get into. Firing
 * immediately spent the first several looks on a handset somewhere between the
 * face and arm's length: not a wrong reading of a constant radius, but a
 * reading of a radius that was not constant, taken while the autofocus that
 * estimates the aperture was being fed.
 *
 * The turn is then **paced audibly** - see `audio/cues.ts` for why a sound is
 * safe here and `dsp/pacing.ts` for the schedule. A user turning at arm's
 * length is not looking at the screen, so a progress bar has never been able
 * to correct a turn that was going wrong. A beat can.
 *
 * And the scan can be **stopped**. It returns whatever it captured rather than
 * throwing it away: a scan abandoned two thirds of the way round is two thirds
 * of a room, which is a thing the reconstruction already knows how to report.
 */

export interface ScanProgress {
  done: number;
  total: number;
  /** Heading swept so far, radians. */
  sweptRad: number;
  /** Heading the schedule asked for by now, radians. */
  idealRad: number;
  verdict: PaceVerdict;
  /** Fraction of the compass visited so far, 0..1. */
  coverage: number;
  pitchSpreadDeg: number;
  remainingSec: number;
}

export interface ScanCaptureOptions {
  preset?: SweepPresetName;
  /** How many chirps to fire. */
  looks?: number;
  /** Gap between the end of one chirp and the start of the next. */
  restSec?: number;
  /** Silence before the first chirp, so the worklet is certainly running. */
  leadInSec?: number;
  /** Extra recording after the last chirp, to catch its tail. */
  tailSec?: number;
  /** The ready beat, in whole seconds. Zero starts immediately. */
  countdownSec?: number;
  playbackGain?: number;
  /** Level of the coaching cues; 0 turns them off. */
  cueVolume?: number;
  haptics?: boolean;
  onStatus?: (status: string) => void;
  /** Counts the ready beat down, `0` meaning the chirping is about to start. */
  onCountdown?: (secondsLeft: number) => void;
  /** Called as each chirp completes, for the progress readout. */
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
}

export interface CapturedLook {
  /** The recording slice holding this chirp and its decay. */
  recording: Float32Array;
  rotation: Mat3;
  timeSec: number;
}

export interface ScanCaptureResult {
  looks: CapturedLook[];
  sampleRate: number;
  sweep: Sweep;
  orientation: OrientationStatus;
  sweptRad: number;
  warnings: string[];
  inputLabel: string;
  /** True when the user stopped it early; the looks are still good. */
  stoppedEarly: boolean;
  /** Chirps the scan meant to fire, against the ones it did. */
  plannedLooks: number;
  /** RMS of the pacing error over the turn, degrees. */
  paceRmsDeg: number;
  timeline: Timeline;
}

/**
 * How long each look's slice needs to be.
 *
 * The chirp itself, plus enough afterwards for the deconvolved impulse
 * response to have decayed - the analysis reads range out of the first tens of
 * milliseconds but the reverberation behind it is what every echo is judged
 * against, so cutting it short truncates the detector's own background.
 */
function sliceSeconds(sweep: Sweep, restSec: number): number {
  return sweep.spec.durationSec + restSec;
}

/** Compass heading of the handset's back, which is what faces the room. */
function headingOf(rotation: Mat3): number {
  const d = applyMat3(rotation, [0, 0, -1]);
  return Math.atan2(d[0], -d[2]);
}

/** Elevation of the same axis, degrees. Nodding is what spreads this. */
function pitchOf(rotation: Mat3): number {
  const d = applyMat3(rotation, [0, 0, -1]);
  const length = Math.hypot(d[0], d[1], d[2]) || 1;
  return (Math.asin(Math.max(-1, Math.min(1, d[1] / length))) * 180) / Math.PI;
}

export async function captureScan(options: ScanCaptureOptions = {}): Promise<ScanCaptureResult> {
  const {
    preset = 'scan',
    looks = 56,
    restSec = 0.13,
    leadInSec = 0.35,
    tailSec = 0.35,
    countdownSec = 3,
    playbackGain = 0.9,
    cueVolume = 1,
    haptics = true,
    onStatus = () => {},
    onCountdown = () => {},
    onProgress = () => {},
    signal,
  } = options;

  const blocker = captureBlocker();
  if (blocker) throw new Error(blocker);

  const warnings: string[] = [];
  onStatus('Requesting microphone...');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: MEASUREMENT_CONSTRAINTS,
    video: false,
  });

  let context: AudioContext | undefined;
  let tracker: OrientationTracker | undefined;
  let cues: CueTrack | undefined;

  try {
    const track = stream.getAudioTracks()[0];
    const settings = track?.getSettings() ?? {};
    if (settings.echoCancellation) warnings.push('Device kept echo cancellation on - echoes will be suppressed.');
    if (settings.autoGainControl) warnings.push('Device kept automatic gain control on - levels will drift across the scan.');
    if (settings.noiseSuppression) warnings.push('Device kept noise suppression on - the reverb tail will be gated.');

    context = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' });
    if (context.state === 'suspended') await context.resume();
    const sampleRate = context.sampleRate;

    onStatus('Loading audio worklet...');
    await loadRecorderModule(context);

    const sweep = generateSweep(sweepSpec(sampleRate, preset));
    const slice = sliceSeconds(sweep, restSec);
    const timeline = scanTimeline({
      looks,
      chirpSec: sweep.spec.durationSec,
      restSec,
      leadInSec,
      tailSec,
      countdownSec,
    });

    cues = createCueTrack(context, { volume: cueVolume, haptics });

    // Orientation is started before the ready beat, not after it, and the
    // epoch is kept explicitly. The tracker reports against its own start, so
    // querying it with times measured from somewhere else - as this did when
    // the two were milliseconds apart and harmless - shears the orientation
    // log away from the chirps by however long the ready beat runs.
    tracker = trackOrientation();
    const trackerEpoch = performance.now() / 1000;
    const sinceTrackerStart = (): number => performance.now() / 1000 - trackerEpoch;

    // The ready beat, counted out loud - the whole point of it is that the
    // phone is already at arm's length and facing away when the first chirp
    // goes out, and someone holding it there is not reading the screen.
    for (let remaining = Math.round(countdownSec); remaining > 0; remaining--) {
      throwIfAborted(signal);
      onCountdown(remaining);
      onStatus(`Arm out, screen towards you. Starting in ${remaining}...`);
      cues.now('count');
      await sleep(1000, signal);
    }
    throwIfAborted(signal);
    onCountdown(0);

    const source = context.createMediaStreamSource(stream);
    const recorder = new AudioWorkletNode(context, 'chirpr-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    const sink = context.createGain();
    sink.gain.value = 0;
    source.connect(recorder).connect(sink).connect(context.destination);

    const chunks: Float32Array[] = [];
    let total = 0;
    recorder.port.onmessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; samples?: Float32Array };
      if (data?.type === 'chunk' && data.samples) {
        chunks.push(data.samples);
        total += data.samples.length;
      }
    };

    const buffer = context.createBuffer(1, sweep.samples.length, sampleRate);
    buffer.getChannelData(0).set(sweep.samples);
    const outputGain = context.createGain();
    outputGain.gain.value = playbackGain;
    outputGain.connect(context.destination);

    // Schedule the whole train up front against the audio clock. Firing each
    // chirp from a setTimeout would accumulate the timer's jitter into the
    // one quantity that has to stay aligned with the orientation log.
    const startAt = context.currentTime + leadInSec;
    const players: AudioBufferSourceNode[] = [];
    for (let k = 0; k < looks; k++) {
      const player = context.createBufferSource();
      player.buffer = buffer;
      player.connect(outputGain);
      player.start(startAt + k * slice);
      players.push(player);
    }

    cues.at(startAt - 0.2, 'go');
    onStatus('Scanning - turn steadily, all the way round, nodding the phone up and down.');

    const beats = new Map(pacingCues(timeline).map((cue) => [cue.lookIndex, cue]));
    const tilts = new Set(tiltCheckpoints(timeline));
    // A cue has to end before the next sweep starts. The slack at the front of
    // the gap keeps it off the tail of the chirp just fired; what is left is
    // the budget the cue vocabulary is written to.
    const gapSlack = 0.012;
    const gapRoom = restSec - gapSlack - 0.015;

    const recordingStart = performance.now() / 1000;
    const rotations: Mat3[] = [];
    const times: number[] = [];
    const covered = new Uint8Array(36);
    let pitchLo = Infinity;
    let pitchHi = -Infinity;
    let paceSquares = 0;
    let paceCount = 0;
    let stoppedEarly = false;

    try {
      for (let k = 0; k < looks; k++) {
        const dueAt = startAt + k * slice + sweep.spec.durationSec * 0.5;
        await waitUntil(context, dueAt, signal);
        // Sample the orientation at the middle of the chirp, which is the pose
        // the look is centred on.
        const rotation = tracker.at(sinceTrackerStart());
        times.push(performance.now() / 1000 - recordingStart);
        rotations.push(rotation);

        const bin = Math.floor(((headingOf(rotation) / (2 * Math.PI) + 1) % 1) * 36) % 36;
        covered[bin] = 1;
        const pitch = pitchOf(rotation);
        pitchLo = Math.min(pitchLo, pitch);
        pitchHi = Math.max(pitchHi, pitch);
        const pitchSpreadDeg = pitchHi > pitchLo ? pitchHi - pitchLo : 0;

        const swept = tracker.sweptRad();
        const ideal = idealHeadingRad(
          timeline,
          chirpStartSec(timeline, k) + sweep.spec.durationSec * 0.5,
        );
        const { error, verdict } = paceVerdict(swept, ideal);
        paceSquares += error * error;
        paceCount++;

        let coveredBins = 0;
        for (const b of covered) coveredBins += b;
        onProgress({
          done: k + 1,
          total: looks,
          sweptRad: swept,
          idealRad: ideal,
          verdict,
          coverage: coveredBins / covered.length,
          pitchSpreadDeg,
          remainingSec: Math.max(0, (looks - k - 1) * slice),
        });

        // Cues go in the gap that follows this chirp, and only if they fit in
        // it - a preset with less rest than a cue is long would otherwise put
        // a tone underneath the next sweep.
        const gapStart = startAt + gapSec(timeline, k)[0];
        const beat = beats.get(k);
        if (tilts.has(k) && pitchSpreadDeg < TILT_TARGET_DEG && cueDurationSec('tilt') <= gapRoom) {
          cues.at(gapStart + gapSlack, 'tilt');
        } else if (beat) {
          const name =
            verdict === 'behind'
              ? 'behind'
              : verdict === 'ahead'
                ? 'ahead'
                : beat.quarter
                  ? 'quarter'
                  : 'beat';
          if (cueDurationSec(name) <= gapRoom) cues.at(gapStart + gapSlack, name);
        }
      }
      await waitUntil(context, startAt + looks * slice + tailSec, signal);
    } catch (error) {
      // A stop is not a failure. What is already in the recorder is a shorter
      // scan, and a shorter scan is something the reconstruction reports
      // honestly rather than something it cannot use.
      if (!isAbort(error)) throw error;
      stoppedEarly = true;
      onStatus('Stopping...');
      // Silence the beat first: pacing cues are scheduled a gap ahead, and
      // ticking on after the user has asked it to stop is the one thing a
      // stop button must not do.
      cues.stop();
      cues = undefined;
      // Let the chirp that was in the air finish arriving before the recorder
      // is cut, so the last look is not half a sweep.
      await sleep(Math.round((sweep.spec.durationSec + tailSec) * 1000));
    }

    recorder.port.postMessage('stop');
    await sleep(60);
    for (const player of players) {
      try {
        player.stop();
      } catch {
        /* already finished */
      }
    }
    source.disconnect();
    recorder.disconnect();
    sink.disconnect();

    const recording = concat(chunks, total);
    if (recording.length === 0) throw new Error('No audio was captured.');

    // Cut the continuous recording into one slice per chirp. The slices are
    // taken generously and each is deconvolved independently, so the exact
    // boundary does not matter - the direct blast inside it is its own
    // reference, exactly as in a single measurement.
    //
    // Cut against the orientations actually logged rather than the chirps
    // scheduled: after a stop those are different numbers, and slicing to the
    // schedule would manufacture looks out of silence the recorder never saw.
    const sliceSamples = Math.round(slice * sampleRate);
    const leadSamples = Math.round(leadInSec * sampleRate);
    const tailSamples = Math.round((sweep.spec.durationSec + restSec + tailSec) * sampleRate);
    const captured: CapturedLook[] = [];
    for (let k = 0; k < rotations.length; k++) {
      const from = Math.max(0, leadSamples + k * sliceSamples - Math.round(0.02 * sampleRate));
      const to = Math.min(recording.length, from + tailSamples);
      if (to - from < sweep.samples.length) continue;
      captured.push({
        recording: recording.subarray(from, to),
        rotation: rotations[k],
        timeSec: times[k],
      });
    }
    if (captured.length === 0) {
      throw new Error(
        stoppedEarly
          ? 'Stopped before a single complete chirp had been recorded.'
          : 'The recording was too short to hold any complete chirp.',
      );
    }

    const orientation = tracker.status();
    if (!orientation.live) warnings.push(orientation.message);
    if (stoppedEarly) {
      warnings.push(
        `Stopped after ${captured.length} of ${looks} chirps. Everything recorded up to that point was used.`,
      );
    }

    const paceRmsDeg = paceCount > 0 ? (Math.sqrt(paceSquares / paceCount) * 180) / Math.PI : 0;
    if (orientation.live && paceCount >= 8 && paceRmsDeg > 45) {
      warnings.push(
        'The turn ran well ahead of or behind the beat, so the phone was not travelling at a steady rate. Following the ticks places surfaces more sharply.',
      );
    }

    return {
      looks: captured,
      sampleRate,
      sweep,
      orientation,
      sweptRad: tracker.sweptRad(),
      warnings,
      inputLabel: track?.label ?? 'microphone',
      stoppedEarly,
      plannedLooks: looks,
      paceRmsDeg,
      timeline,
    };
  } finally {
    cues?.stop();
    tracker?.stop();
    for (const track of stream.getTracks()) track.stop();
    if (context && context.state !== 'closed') void context.close();
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Scan stopped', 'AbortError');
}

/** Wait until the audio context's own clock reaches `when`. */
function waitUntil(context: AudioContext, when: number, signal?: AbortSignal): Promise<void> {
  const remaining = Math.max(0, when - context.currentTime);
  return sleep(remaining * 1000, signal);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Scan stopped', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('Scan stopped', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function loadRecorderModule(context: AudioContext): Promise<void> {
  try {
    await context.audioWorklet.addModule(recorderWorkletUrl);
  } catch (error) {
    const response = await fetch(recorderWorkletUrl);
    if (!response.ok) throw error;
    const source = await response.text();
    const blobUrl = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
    try {
      await context.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }
}

function concat(chunks: Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
